// Pure model of the tasks board (Kanban, route #/taches): the columns, what a card may move to
// (and the API call each move makes), the small badges and lines of a card, which live task events
// change the board, and the optimistic move. DOM-free (tested with node:test).
import { t } from '../../i18n/fr.js';
import { relativeTime, formatDateTime } from '../../format.js';
import './strings.js';

/** The columns, in display order, with their tone (dot and accents). */
export const COLUMNS = Object.freeze([
  { id: 'todo', tone: 'info' },
  { id: 'doing', tone: 'running' },
  { id: 'review', tone: 'amber' },
  { id: 'blocked', tone: 'blocked' },
  { id: 'failed', tone: 'danger' },
  { id: 'done', tone: 'success' },
  { id: 'ignored', tone: 'muted' },
]);
export const COLUMN_IDS = Object.freeze(COLUMNS.map((c) => c.id));
/** Periods of the finished work (days). */
export const PERIODS = Object.freeze([1, 7, 30]);
export const DEFAULT_DAYS = 7;
const RETRYABLE = new Set(['failed', 'cancelled', 'rejected']);

/** The French name of a column. */
export function columnLabel(id) {
  return t(`kb.col.${id}`);
}

/**
 * The moves a card offers (drag & drop targets and the « Déplacer vers… » menu), for a person who
 * may act on its department (operate). Each move is {target, action, chain}: retry, ignore, cancel,
 * unignore or settle; `chain` when it applies to the other runs of the routine on the card too.
 * @param {object} card a board card (server/tasks/board.js)
 * @param {{canOperate?: boolean}} [opts]
 * @returns {{target: string, action: string, chain: boolean}[]}
 */
export function movesFor(card, { canOperate = false } = {}) {
  if (!card || !canOperate) return [];
  const grouped = (card.group?.count || 0) > 1;
  const out = [];
  const add = (target, action, chain = false) => out.push({ target, action, chain });
  const retryable = RETRYABLE.has(card.state) || (card.state === 'done' && card.blocked);
  switch (card.column) {
    case 'todo':
    case 'doing':
      add('ignored', 'cancel');
      break;
    case 'review':
      add('ignored', 'ignore');
      if (card.todo) add('done', 'settle');
      break;
    case 'blocked':
    case 'failed':
      add('todo', 'retry');
      add('ignored', 'ignore', grouped);
      break;
    case 'ignored':
      if (card.ignored && card.restore && card.restore !== 'ignored') add(card.restore, 'unignore', grouped);
      if (retryable) add('todo', 'retry');
      break;
    default:
      break;
  }
  return out;
}

/** The move a card makes when dropped on `target` (null when it cannot go there). */
export function moveTo(card, target, opts) {
  return movesFor(card, opts).find((m) => m.target === target) || null;
}

/**
 * The menu label of a move: « En erreur — ne plus ignorer », « Ignorées — annuler la tâche »…
 * @param {object} card
 * @param {{target: string, action: string, chain: boolean}} move
 */
export function moveLabel(card, move) {
  const column = columnLabel(move.target);
  let key = `kb.move.${move.action}`;
  if (move.action === 'ignore' && card.state === 'awaiting_approval') key = 'kb.move.ignoreDraft';
  else if (move.action === 'ignore' && card.column === 'review') key = 'kb.move.ignoreDecision';
  else if (move.action === 'cancel' && card.column === 'doing') key = 'kb.move.cancelRunning';
  const label = t(key, { column });
  const others = (card.group?.count || 1) - 1;
  return move.chain && others > 0 ? `${label} ${t('kb.move.chain', { n: others })}` : label;
}

/**
 * The API call of a move: {method, path, body}.
 * @param {object} card
 * @param {{action: string, chain: boolean}} move
 */
export function moveRequest(card, move) {
  const base = `/api/tasks/${encodeURIComponent(card.id)}`;
  switch (move.action) {
    case 'retry': return { path: `${base}/retry`, body: {} };
    case 'cancel': return { path: `${base}/cancel`, body: {} };
    case 'ignore': return { path: `${base}/ignore`, body: move.chain ? { ignored: true, chain: true } : { ignored: true } };
    case 'unignore': return { path: `${base}/ignore`, body: move.chain ? { ignored: false, chain: true } : { ignored: false } };
    case 'settle': return { path: '/api/notifications/resolve', body: { ids: [`task:${card.id}`] } };
    default: return null;
  }
}

/** The move that undoes another one (ignore ↔ unignore), or null. */
export function undoOf(card, move) {
  if (move.action === 'ignore') return { target: card.column, action: 'unignore', chain: move.chain };
  if (move.action === 'unignore') return { target: 'ignored', action: 'ignore', chain: move.chain };
  return null;
}

/**
 * A copy of the board with one card moved to the top of another column (the optimistic move,
 * replaced by the server's board right after). Counts follow.
 * @param {object} board GET /api/tasks/board
 * @param {string} cardId
 * @param {string} target
 */
export function withCardMoved(board, cardId, target) {
  if (!board?.columns) return board;
  let card = null;
  const columns = board.columns.map((c) => {
    const i = c.cards.findIndex((x) => x.id === cardId);
    if (i < 0) return c;
    card = c.cards[i];
    return { ...c, cards: c.cards.filter((_, j) => j !== i), count: Math.max(0, c.count - 1) };
  });
  if (!card || !columns.some((c) => c.id === target)) return board;
  return {
    ...board,
    columns: columns.map((c) => (c.id === target
      ? { ...c, cards: [{ ...card, column: target, pending: true }, ...c.cards], count: c.count + 1 }
      : c)),
  };
}

/**
 * The time line of a card (« prévue demain à 08:00 », « depuis 4 min », « il y a 2 h »).
 * @param {object} card
 * @param {number} [now]
 */
export function cardTime(card, now = Date.now()) {
  const at = card?.at;
  if (!at) return '';
  // « depuis 4 min » for what is going on; under a minute, « à l’instant ».
  const since = (ms, key) => (now - ms < 60_000 ? t('kb.time.justNow') : t(key, { when: relativeTime(ms, now, { short: true }) }));
  switch (card.column) {
    case 'todo':
      return card.state === 'scheduled' ? t('kb.time.scheduled', { when: formatDateTime(card.dueAt || at, now) }) : since(at, 'kb.time.queued');
    case 'doing': return since(card.startedAt || at, 'kb.time.running');
    case 'review': return since(at, 'kb.time.waiting');
    default: return relativeTime(at, now);
  }
}

/**
 * The badges of a card, in display order: {text, tone} with tone neutral | amber | danger | success | info | muted.
 * @param {object} card
 * @param {number} [now]
 * @returns {{text: string, tone: string, kind: string}[]}
 */
export function cardBadges(card, now = Date.now()) {
  const out = [];
  const add = (kind, text, tone = 'neutral') => out.push({ kind, text, tone });
  const attempts = Number(card.attempts) || 0;
  const group = card.group || null;
  if (card.late) add('late', t('kb.badge.late'), 'amber');
  if (card.state === 'scheduled') add('state', t('kb.badge.scheduled'), 'info');
  if (card.state === 'executing') add('state', t('kb.badge.executing'), 'info');
  if (card.column === 'review') add('state', card.todo ? t('kb.badge.decision') : t('kb.badge.draft'), 'amber');
  // The attempt in progress or waiting (« 3e essai »), how many it took (« Réussie au 3e essai »),
  // how many failed (« 3 essais »).
  if (['todo', 'doing', 'review'].includes(card.column) && attempts > 0 && card.state !== 'done') {
    add('attempt', t('kb.badge.attempt', { nth: ordinal(attempts + 1) }), 'amber');
  } else if (card.column === 'done' && card.state === 'done' && attempts > 1) {
    add('attempt', t('kb.badge.succeededAt', { nth: ordinal(attempts) }), 'success');
  } else if ((card.column === 'failed' || card.column === 'blocked') && attempts > 1) {
    add('attempt', t('kb.badge.attempts', { n: attempts }), 'danger');
  }
  if (card.superseded && card.state !== 'done') add('resolved', t('kb.badge.resolvedLater'), 'success');
  if (group && group.count > 1) {
    if (card.column === 'failed') add('group', t('kb.badge.earlierFailed', { n: group.earlier }), 'danger');
    else if (card.column === 'blocked') add('group', t('kb.badge.earlierBlocked', { n: group.earlier }), 'amber');
    else if (card.column === 'ignored') add('group', t('kb.badge.earlierIgnored', { n: group.earlier }), 'muted');
  }
  if (card.column === 'done' && group) {
    if (group.runs > 1) add('runs', t('kb.badge.runs', { n: group.runs }), 'neutral');
    if (group.resolved > 0) add('resolved', t('kb.badge.resolved', { n: group.resolved }), 'success');
  }
  if (card.nextTry) add('next', t('kb.badge.nextTry', { when: relativeTime(card.nextTry, now) }), 'info');
  if (card.column === 'ignored') {
    if (card.ignored) add('ignored', card.ignored.byName ? t('kb.badge.ignoredBy', { name: card.ignored.byName }) : t('kb.badge.ignored'), 'muted');
    else if (card.settled) add('settled', card.settled.byName ? t('kb.badge.settledBy', { name: card.settled.byName }) : t('kb.badge.settled'), 'muted');
    else if (card.state === 'cancelled') add('state', t('kb.badge.cancelled'), 'muted');
    else if (card.state === 'rejected') add('state', t('kb.badge.rejected'), 'muted');
  }
  return out;
}

/**
 * The short line of a card and its tone: the error, the blocking, the decision to take, the
 * start of the result.
 * @param {object} card
 * @returns {{text: string, tone: string}}
 */
export function cardLine(card) {
  const text = String(card?.line || '').trim();
  switch (card?.lineKind) {
    case 'error': return { text, tone: card.column === 'failed' ? 'danger' : 'muted' };
    case 'blocked': return { text, tone: card.column === 'blocked' ? 'amber' : 'muted' };
    case 'todo': return { text, tone: card.column === 'review' ? 'amber' : 'muted' };
    case 'draft': return { text: t('kb.line.draft'), tone: 'amber' };
    case 'idle': return { text: t('kb.line.idle'), tone: 'muted' };
    case 'result': return { text, tone: 'muted' };
    default: return { text, tone: 'muted' };
  }
}

/** « 1er », « 2e »… */
export function ordinal(n) {
  const k = Math.max(1, Math.floor(Number(n) || 1));
  return k === 1 ? '1er' : `${k}e`;
}

/**
 * What a live task summary must match to change the board: its column-deciding fields.
 * @param {object} task TaskSummary or board card source
 */
export function signature(task) {
  if (!task) return '';
  return [task.state, !!task.blocked, !!task.todo, !!task.settled, !!task.ignored, !!task.superseded, task.idle === true].join('|');
}

/**
 * Does a live `task` event change the board shown? A task of another floor or department never;
 * an idle pass only when its card is on screen; a known card only when its column may change.
 * @param {object} task TaskSummary
 * @param {{floorId?: string, deptId?: string}} scope
 * @param {Map<string, string>} known card id → signature of the task it shows
 */
export function isRelevant(task, { floorId = '', deptId = '' } = {}, known = new Map()) {
  if (!task || typeof task !== 'object' || !task.id) return false;
  if (floorId && task.floorId !== floorId) return false;
  if (deptId && task.deptId !== deptId) return false;
  if (known.has(task.id)) return known.get(task.id) !== signature(task);
  return task.idle !== true;
}

/** The signature a board card stands for (same fields as `signature`, from the card's own). */
export function cardSignature(card) {
  return signature({
    state: card.state, blocked: card.blocked, todo: card.todo, settled: card.settled, ignored: card.ignored, superseded: card.superseded,
  });
}

/** The board's total and its errors, for the toolbar. */
export function boardSummary(board) {
  const cols = board?.columns || [];
  const tasks = cols.reduce((n, c) => n + (Number(c.taskCount) || 0), 0);
  const errors = cols.filter((c) => c.id === 'failed' || c.id === 'blocked').reduce((n, c) => n + (Number(c.taskCount) || 0), 0);
  return { tasks, errors };
}

/**
 * Query of GET /api/tasks/board.
 * @param {{floorId?: string, deptId?: string, days?: number, expand?: Iterable<string>}} st
 */
export function boardParams({ floorId = '', deptId = '', days = DEFAULT_DAYS, expand = [] } = {}) {
  const out = { days };
  if (floorId) out.floorId = floorId;
  if (deptId) out.deptId = deptId;
  const list = [...expand].filter((c) => COLUMN_IDS.includes(c));
  if (list.length) out.expand = list.join(',');
  return out;
}
